// eslint-disable-next-line no-restricted-imports
import {useFlags} from 'launchdarkly-react-client-sdk';

import {isCsmDeployment} from '~utils/deploymentEnv';
import {shouldForceAllFlagsEnabled} from '~utils/launchDarklyEnv';

export type FeatureFlags = {
  toggleEnableProjectCreation: boolean;
  toggleEnableSimSearch: boolean;
  toggleEnableTrainingConfiguration: boolean;
  toggleEnableDockerDeployment: boolean;
  toggleEnableInsights: boolean;
  toggleEnablePreTrainingCheck: boolean;
  toggleEnableAskAiWidget: boolean;
  internalUserTest: boolean;
  toggleEnableMonitorLiveMode: boolean;
  toggleEnableMeasuringApp: boolean;
  toggleEnableDefectStudio: boolean;
};

/**
 * Flag values used when running locally without LaunchDarkly.
 * Typed as `FeatureFlags` so adding a flag to the type is a compile error until it is listed here.
 */
const devFeatureFlags: FeatureFlags = {
  toggleEnableProjectCreation: true,
  toggleEnableSimSearch: true,
  toggleEnableTrainingConfiguration: true,
  toggleEnableDockerDeployment: true,
  toggleEnableInsights: true,
  toggleEnablePreTrainingCheck: true,
  toggleEnableAskAiWidget: true,
  internalUserTest: true,
  toggleEnableMonitorLiveMode: true,
  toggleEnableMeasuringApp: true,
  toggleEnableDefectStudio: true,
};

/**
 * The CSM deployment (csm.maddox.ai) runs with every flag switched on, without LaunchDarkly. List a
 * flag here only when it must stay OFF for the CSMs even though the feature is in the release — a
 * code change, deliberately, instead of a LaunchDarkly rule nobody remembers.
 */
const csmFeatureFlagOverrides: Partial<FeatureFlags> = {};

const forcedFeatureFlags: FeatureFlags = isCsmDeployment
  ? {...devFeatureFlags, ...csmFeatureFlagOverrides}
  : devFeatureFlags;

export const useFeatureFlags = (): FeatureFlags => {
  const flags = useFlags<FeatureFlags>();
  if (shouldForceAllFlagsEnabled) {
    return forcedFeatureFlags;
  }
  return flags;
};
